<template>
  <div class="flex h-full min-w-0 flex-col">
    <!-- Coverage tabs -->
    <div class="flex items-stretch border-b border-border">
      <SButton
        type="button"
        variant="ghost"
        size="lg"
        :class="[
          'discount-quantity-btn rounded-none border-b-2 border-transparent -mb-px hover:bg-transparent',
          isDiscountTypePercentage ? 'flex-1' : 'w-full',
          discountInfo.isOnAllOrder
            ? '!border-primary !text-primary'
            : '!text-muted-foreground',
        ]"
        @click="makeDiscountOnAllOrder"
      >
        {{ $t("discount.all-order") }}
      </SButton>
      <SButton
        v-if="isDiscountTypePercentage"
        type="button"
        variant="ghost"
        size="lg"
        :class="[
          'discount-quantity-btn flex-1 rounded-none border-b-2 border-transparent -mb-px hover:bg-transparent',
          !discountInfo.isOnAllOrder
            ? '!border-primary !text-primary'
            : '!text-muted-foreground',
        ]"
        @click="UpdateDiscountApplicationCoverage"
      >
        {{ $t("discount.specific-tickets") }}
      </SButton>
    </div>

    <!-- Discount type + values -->
    <div class="flex min-w-0 flex-col gap-5 py-6 md:flex-row md:gap-8">
      <div class="flex w-full shrink-0 flex-col gap-3 md:w-52">
        <SButton
          type="button"
          class="discount-type-btn w-full"
          :variant="isDiscountTypePercentage ? 'default' : 'outline'"
          @click="updateDiscountType(DiscountType.PERCENT)"
        >
          {{ $t("discount.choose-percentage").toUpperCase() }}
        </SButton>
        <SButton
          type="button"
          class="discount-type-btn w-full"
          :variant="
            discountInfo.type === DiscountType.FLAT ? 'default' : 'outline'
          "
          @click="updateDiscountType(DiscountType.FLAT)"
        >
          {{ $t("discount.choose-fixed-value").toUpperCase() }}
        </SButton>
      </div>
      <div class="flex min-w-0 flex-1 flex-col gap-3">
        <div class="grid grid-cols-3 gap-3">
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 5 ? 'default' : 'outline'"
            @click="updateDiscountValue(5)"
            data-cy="discount-5"
          >
            {{ isDiscountTypePercentage ? "5%" : currency + " 5" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 10 ? 'default' : 'outline'"
            @click="updateDiscountValue(10)"
            data-cy="discount-10"
          >
            {{ isDiscountTypePercentage ? "10%" : currency + " 10" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 15 ? 'default' : 'outline'"
            @click="updateDiscountValue(15)"
          >
            {{ isDiscountTypePercentage ? "15%" : currency + " 15" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 30 ? 'default' : 'outline'"
            @click="updateDiscountValue(30)"
            data-cy="discount-30"
          >
            {{ isDiscountTypePercentage ? "30%" : currency + " 30" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 50 ? 'default' : 'outline'"
            @click="updateDiscountValue(50)"
            data-cy="discount-50"
          >
            {{ isDiscountTypePercentage ? "50%" : currency + " 50" }}
          </SButton>
          <SButton
            type="button"
            class="discount-value-btn"
            :variant="discountInfo.value === 100 ? 'default' : 'outline'"
            @click="updateDiscountValue(100)"
            data-cy="discount-100"
          >
            {{ isDiscountTypePercentage ? "100%" : currency + " 100" }}
          </SButton>
        </div>
        <SButton
          type="button"
          variant="outline"
          size="icon"
          class="h-[52px] w-full"
          aria-label="open calculator"
          @click="showCalculator"
          data-cy="discount-calculator-button"
        >
          <SvgIcon
            :iconContent="calcIcon"
            :iconAlt="calcAlt"
            :iconStyle="calcIconStyle"
          />
        </SButton>
      </div>
    </div>

    <!-- Summary + actions -->
    <div class="mt-auto">
      <SSeparator />
      <div
        class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pt-4"
      >
        <div
          class="grid grid-cols-[auto_auto_minmax(3.5rem,auto)] gap-x-4 gap-y-1 text-sm"
        >
          <span class="font-medium">{{ t("button.discount") }}</span>
          <span class="text-muted-foreground">{{ currency }}</span>
          <span
            class="text-right font-semibold tabular-nums"
            data-cy="discount-preview-amount"
          >
            {{ (discountResult / 100).toFixed(2) }}
          </span>
          <span class="font-medium">
            {{ t("completeorder.order-total") }}
          </span>
          <span class="text-muted-foreground">{{ currency }}</span>
          <span
            class="text-right font-semibold tabular-nums"
            data-cy="discount-preview-total"
            >{{ total }}</span
          >
        </div>
        <div class="flex gap-2">
          <SButton
            type="button"
            class="h-12 min-w-[110px]"
            :loading="isDiscountLoading"
            :disabled="isDiscountLoading"
            @click="applyDiscount"
            data-cy="apply-discount-button"
          >
            {{ $t("promocode.apply") }}
          </SButton>
          <SButton
            type="button"
            class="h-12 min-w-[110px]"
            :variant="isDiscountApplied ? 'destructive' : 'outline'"
            :loading="isDiscountLoading"
            :disabled="!isDiscountApplied || isDiscountLoading"
            @click="onRemoveDiscount"
            data-cy="remove-discount-button"
          >
            {{ $t("common.remove") }}
          </SButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import ModalModule from "@/store/modules/modal";
import { ModalStatus } from "@/constants/modal";
import calculatorSvg from "@/assets/images/svg/calculator-sign.svg";
import { Currencies } from "@/constants/currency";
import { LineType, OrderLine } from "@smeetz/order-common";
import { useOrderStore } from "@smeetz/order-store";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import { DiscountType } from "@smeetz/order-common";
import {
  applyManualDiscount,
  removeDiscount,
} from "@/utils/vue-helpers/orders.helper";
import { SButton, SSeparator } from "@smeetz/ui-core";

interface IDiscountInfo {
  type: number;
  value: number;
  isOnAllOrder: boolean;
  bookingId: string;
}

export default defineComponent({
  name: "app",
  components: { SButton, SSeparator },
  setup() {
    const { t } = useI18n({});
    const orderStore = useOrderStore();
    const orderMetaStore = useOrderMetaStore();

    const calcIcon = calculatorSvg;
    const calcAlt = "calculator";
    const calcIconStyle = "width: 30px; height: 30px;";

    const orderSession = computed(() => orderStore.selectedOrder?.orderSession);
    const selectedType = ref<DiscountType | null>(null);
    const selectedValue = ref<number | null>(null);
    const selectedCoverage = ref<boolean | null>(null);
    const isDiscountLoading = ref(false);

    const discountInfo = computed(() => {
      const manualDiscount = getExistingManualDiscount();

      return {
        type:
          selectedType.value ?? manualDiscount?.type ?? DiscountType.PERCENT,
        value: selectedValue.value ?? Number(manualDiscount?.value || 0),
        isOnAllOrder:
          selectedCoverage.value ??
          (manualDiscount ? !manualDiscount.targetOrderLineIds.length : true),
        bookingId: orderStore.selectedOrder?.id || "",
      } as IDiscountInfo;
    });

    const currency = computed(() => {
      return orderSession.value?.currency || Currencies.CHF;
    });

    const isDiscountApplied = computed(() => {
      return !!getExistingManualDiscount();
    });

    const isDiscountTypePercentage = computed(() => {
      return discountInfo.value.type === DiscountType.PERCENT;
    });

    function sumOrderLineCents(lines: OrderLine[]): number {
      const total = lines
        .filter((line) => line.lineType !== LineType.CancelLine)
        .reduce(
          (sum, line) =>
            sum +
            Number(
              line.totalPrice ??
                line.totalPriceWithDiscount ??
                line.unitPrice ??
                0
            ),
          0
        );
      return Math.round(total * 100);
    }

    // Base used for discount calculation — scoped to selected lines when the
    // user picked "specific items"; full order otherwise.
    const previewBaseCents = computed(() => {
      const orderLines = orderSession.value?.orderLines || [];
      const isOnAllOrder = discountInfo.value.isOnAllOrder !== false;
      const selectedPriceIds = orderMetaStore.discountPriceIds;

      const filtered = isOnAllOrder
        ? orderLines
        : orderLines.filter(
            (line) => line.priceId && selectedPriceIds.includes(line.priceId)
          );
      return sumOrderLineCents(filtered);
    });

    // Full order base — used for the "Total" display so it always reflects
    // the full order minus the (possibly scoped) discount.
    const fullOrderBaseCents = computed(() =>
      sumOrderLineCents(orderSession.value?.orderLines || [])
    );

    const previewDiscountCents = computed(() => {
      const baseCents = previewBaseCents.value;
      const discountValue = Number(discountInfo.value.value);

      if (discountInfo.value.type === DiscountType.PERCENT) {
        return Math.round((baseCents * discountValue) / 100);
      }

      return Math.round(discountValue * 100);
    });

    const discountResult = computed(() => {
      return previewDiscountCents.value;
    });

    function updateDiscountType(typeValue: DiscountType) {
      selectedType.value = typeValue;
    }

    function updateDiscountValue(val: number) {
      selectedValue.value = val;
    }

    function UpdateDiscountApplicationCoverage() {
      selectedCoverage.value = false;
      ModalModule.setStatus(ModalStatus.DiscountSelectTickets);
    }

    function makeDiscountOnAllOrder() {
      selectedCoverage.value = true;
      orderMetaStore.clearDiscountPriceIds();
    }

    function showCalculator() {
      ModalModule.setStatus(ModalStatus.PaymentCalculator);
    }

    function getExistingManualDiscount() {
      const od = orderSession.value?.orderDiscounts?.find(
        (d) => d.type === DiscountType.FLAT || d.type === DiscountType.PERCENT
      );
      if (!od) return null;

      return {
        type: od.type,
        value:
          od.type === DiscountType.PERCENT
            ? (od.percentDiscount ?? 0) * 100
            : od.flatDiscount ?? 0,
        targetOrderLineIds: od.targetOrderLineIds ?? [],
        orderDiscountId: od.id,
      };
    }

    async function applyDiscount() {
      isDiscountLoading.value = true;

      try {
        const existingManualDiscount = getExistingManualDiscount();
        if (existingManualDiscount?.orderDiscountId) {
          await removeDiscount(existingManualDiscount.orderDiscountId);
        }

        const selectedPriceIds = orderMetaStore.discountPriceIds;
        const useSpecificTickets =
          discountInfo.value.isOnAllOrder === false &&
          selectedPriceIds.length > 0;

        const targetOrderLineIds = useSpecificTickets
          ? (orderSession.value?.orderLines ?? [])
              .filter(
                (line) =>
                  line.priceId && selectedPriceIds.includes(line.priceId)
              )
              .map((line) => line.id)
          : undefined;

        await applyManualDiscount(
          discountInfo.value.value,
          discountInfo.value.type,
          targetOrderLineIds
        );
      } catch (error) {
        console.error("Failed to apply discount:", error);
      } finally {
        isDiscountLoading.value = false;
      }
    }

    async function onRemoveDiscount() {
      const targetId = getExistingManualDiscount()?.orderDiscountId;
      if (!targetId) return;

      isDiscountLoading.value = true;
      try {
        await removeDiscount(targetId);
        orderMetaStore.clearDiscountPriceIds();
      } catch (error) {
        console.error("Failed to remove discount:", error);
      } finally {
        isDiscountLoading.value = false;
      }
    }

    const total = computed(() => {
      const totalCents = fullOrderBaseCents.value - previewDiscountCents.value;
      return (totalCents / 100).toFixed(2);
    });

    return {
      t,
      calcIcon,
      calcAlt,
      calcIconStyle,
      discountInfo,
      isDiscountTypePercentage,
      DiscountType,
      discountResult,
      isDiscountApplied,
      isDiscountLoading,
      currency,
      showCalculator,
      updateDiscountType,
      updateDiscountValue,
      UpdateDiscountApplicationCoverage,
      makeDiscountOnAllOrder,
      applyDiscount,
      onRemoveDiscount,
      total,
    };
  },
});
</script>

<style lang="scss" scoped>
.discount-type-btn {
  height: 52px;
  font-size: 14px;
  font-weight: 500;
}

.discount-value-btn {
  height: 52px;
  width: 100%;
}

.discount-quantity-btn {
  font-size: 16px;
  font-weight: 500;
}
</style>
